import { useRef, useState } from 'react'
import {
  Alert,
  Button,
  FileButton,
  Group,
  Input,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { IconAlertCircle, IconCamera, IconCheck, IconUserCheck } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { MINIMUM_PASSWORD_LENGTH as MINIMUM_PASSWORD, fetchDialCodes, setOwnPassword, updateProfile, uploadAvatar } from '../api/session'
import { sessionQueryOptions } from '../session-store'
import type { CurrentUser } from '../api/types'
import { AvatarCropperField } from './AvatarCropperField'
import { UserAvatar } from './UserAvatar'

/**
 * Asks for the things signing in with Google could not supply.
 *
 * Google returns a name, an address and a photograph, and those are taken
 * automatically. It returns no telephone number under the scopes this app
 * asks for, and an account signed into with Google has no password - so those
 * two are asked for here, and only when they are actually missing.
 *
 * A picture is asked for too when there is none. Google does not always have
 * one, and the fetch gives up quietly rather than fail a sign-in - and a board
 * of grey initials is a board where nobody can tell at a glance who has what.
 *
 * Dismissable, and asked again next time. A modal nobody can escape is one
 * people learn to resent, and neither of these stops somebody working today -
 * but a precaution nobody is reminded of is a precaution nobody takes.
 */
export function CompleteProfilePrompt({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient()
  const [dismissed, setDismissed] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [dialCode, setDialCode] = useState(user.phone_dial_code ?? '')
  const [phone, setPhone] = useState(user.phone_number ?? '')
  const [error, setError] = useState<string | null>(null)
  const [cropping, setCropping] = useState<File | null>(null)
  const resetFile = useRef<() => void>(null)

  /*
    Decided once, when the prompt first renders, rather than read live. The
    upload updates the session straight away, and a live reading would make
    the whole section vanish the moment it succeeded - taking with it the
    preview that shows somebody the picture they just chose.
  */
  const [needsPicture] = useState(() => !user.has_avatar)

  const needsPassword = !user.has_password
  /*
    A number with no country code counts as missing. It is how the old
    free-text field left them, and a number nobody can dial from abroad is
    not a contact detail - it is a string of digits.
  */
  const needsPhone = (user.phone_number ?? '') === '' || (user.phone_dial_code ?? '') === ''

  const dialCodes = useQuery({
    queryKey: ['dial-codes'] as const,
    queryFn: ({ signal }) => fetchDialCodes(signal),
    enabled: needsPhone,
    staleTime: 60 * 60_000,
  })

  /*
    Uploaded as soon as the crop is confirmed, not held for Save: cropping is
    already an explicit "yes, this one", and holding a file in the dialog is
    a file somebody loses by pressing Not now.
  */
  const setPicture = useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: (updated) => {
      if (updated) queryClient.setQueryData(sessionQueryOptions.queryKey, updated)
    },
    onError: () => setError('That picture could not be uploaded. Try a PNG, JPEG, WebP or GIF.'),
    onSettled: () => setCropping(null),
  })

  const save = useMutation({
    mutationFn: async () => {
      // Two calls because they are two different endpoints, and the password
      // one re-establishes the session - so it goes last, or the profile
      // update would travel on a session that has just been replaced.
      if (needsPhone && phone !== '') {
        await updateProfile({ phone, phone_dial_code: dialCode })
      }

      if (needsPassword) {
        // No current password: there is none. The server applies the same
        // rule rather than taking this on trust.
        await setOwnPassword(password)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQueryOptions.queryKey })
      setDismissed(true)
    },
    onError: (failure) => {
      setError(
        failure instanceof ApiError && failure.status < 500
          ? 'Something there was refused. Check the password length and the number.'
          : 'Could not save your details. Try again.',
      )
    },
  })

  const passwordTooShort = password.length > 0 && password.length < MINIMUM_PASSWORD
  const mismatched = confirmation.length > 0 && confirmation !== password
  const phoneIncomplete = phone !== '' && dialCode === ''

  const passwordReady = !needsPassword || (password.length >= MINIMUM_PASSWORD && confirmation === password)
  const phoneReady = !needsPhone || phone === '' || dialCode !== ''

  return (
    <Modal
      opened={(needsPassword || needsPhone || needsPicture) && !dismissed}
      onClose={() => setDismissed(true)}
      title={
        <Group gap={8}>
          <IconUserCheck size={18} />
          <Text fw={600}>Finish setting up your account</Text>
        </Group>
      }
      centered
      size="sm"
    >
      <Stack gap="md">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" role="alert">
            {error}
          </Alert>
        )}

        {needsPassword && (
          <>
            <Text size="sm" c="dimmed">
              You signed in with Google, so this account has no password yet.
              Setting one means you can still get in if Google is unavailable.
            </Text>

            <PasswordInput
              label="New password"
              autoComplete="new-password"
              autoFocus
              value={password}
              error={passwordTooShort ? `At least ${MINIMUM_PASSWORD} characters` : null}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />

            <PasswordInput
              label="Confirm password"
              autoComplete="new-password"
              value={confirmation}
              error={mismatched ? 'The two do not match' : null}
              onChange={(event) => setConfirmation(event.currentTarget.value)}
            />
          </>
        )}

        {needsPicture && (
          <>
            <Text size="sm" c="dimmed">
              {user.has_avatar
                ? 'That is you on every card and comment from now on.'
                : 'Add a picture so colleagues can tell at a glance whose work is whose.'}
            </Text>

            <Group gap="md" wrap="nowrap">
              <UserAvatar
                userId={user.id}
                initials={user.initials}
                hasAvatar={user.has_avatar}
                size={56}
                // Busted on upload, so the new picture replaces the initials
                // at once rather than after the old response expires.
                version={user.updated_at}
              />
              <FileButton
                resetRef={resetFile}
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(file) => {
                  if (file) setCropping(file)
                  // Cleared at once, so choosing the same file twice still
                  // opens the cropper.
                  resetFile.current?.()
                }}
              >
                {(props) => (
                  <Button
                    {...props}
                    variant="light"
                    leftSection={user.has_avatar ? <IconCheck size={15} /> : <IconCamera size={15} />}
                    loading={setPicture.isPending}
                  >
                    {user.has_avatar ? 'Choose a different one' : 'Upload a picture'}
                  </Button>
                )}
              </FileButton>
            </Group>
          </>
        )}

        {needsPhone && (
          <>
            <Text size="sm" c="dimmed">
              Google does not share a telephone number, so this is the one
              thing we cannot fill in for you.
            </Text>

            {/* Two fields, because a number is two things: which country and
                which number. As one box everybody writes a different thing. */}
            <Input.Wrapper
              label="Phone"
              error={phoneIncomplete ? 'Pick the country as well' : null}
            >
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Select
                  w={140}
                  searchable
                  placeholder="Country"
                  aria-label="Country code"
                  data={dialCodes.data?.map((entry) => ({
                    value: entry.code,
                    label: `${entry.code} ${entry.country}`,
                  }))}
                  value={dialCode || null}
                  onChange={(value) => setDialCode(value ?? '')}
                />
                <TextInput
                  style={{ flex: 1 }}
                  placeholder="Digits only"
                  aria-label="Phone number"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phone}
                  // Stripped as it is typed: pasting a formatted number
                  // should work, and being told off for it should not be the
                  // first thing that happens.
                  onChange={(event) =>
                    setPhone(event.currentTarget.value.replace(/\D/g, '').slice(0, 15))
                  }
                />
              </Group>
            </Input.Wrapper>
          </>
        )}

        <Group justify="space-between">
          <Button variant="subtle" color="gray" onClick={() => setDismissed(true)}>
            Not now
          </Button>
          <Button
            loading={save.isPending}
            disabled={!passwordReady || !phoneReady}
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            Save
          </Button>
        </Group>
      </Stack>

      {/* The same crop-to-512px step the profile page uses, so a picture
          chosen here is checked and shaped exactly as one chosen there. */}
      <AvatarCropperField
        file={cropping}
        saving={setPicture.isPending}
        onCancel={() => setCropping(null)}
        onConfirm={(cropped) => setPicture.mutate(cropped)}
      />
    </Modal>
  )
}
